import React, {useEffect, useState} from 'react';
import Card from '../../../../Presentation/components/ui/Card';
import {AppText, AppTitle, AppCardTitle} from '../../../../Presentation/components/ui/Typography';
import Button from '../../../../Presentation/components/ui/Button';
import {DashboardUser} from '../../models/user.model';
import {PermissionsTable} from '../tables/PermissionsTable';
import {PermissionModal} from '../modals/PermissionModal';
import {usePermissions} from '../../hooks/usePermissions';
import {Permission, PermissionFilters} from '../../models/permission.model';
import {useNotification} from '../../../../Services/useNotification';
import {Plus, Shield} from 'lucide-react';
import {useUserManagement} from '../../hooks/useUserManagement';
import StandardizedErrorHandler from '../../../../Services/common/StandardizedErrorHandler';

interface PermissionsManagementProps {
  users: DashboardUser[];
  onAssignPermissions?: (userId: number, permissions: string[]) => Promise<void>;
}

export const PermissionsManagementNew: React.FC<PermissionsManagementProps> = ({
  users: _users,
  onAssignPermissions: _onAssignPermissions
}) => {
  // Local modal state to avoid conflicts
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<Permission | undefined>(undefined);

  const {
    // UI State
    isPermissionsModalOpen,
    selectedPermission: globalSelectedPermission,
    setSelectedPermission: globalSetSelectedPermission,

    // Modal Actions
    openPermissionsModal,
    closePermissionsModal
  } = useUserManagement();

  const {success: showSuccess} = useNotification();

  const {
    permissions,
    pagination,
    loading,
    creating,
    updating,
    error,
    createError,
    updateError,
    deleteError,
    fetchPermissions,
    createPermission,
    updatePermission,
    deletePermission,
    clearError,
    clearCreateError,
    clearUpdateError,
    clearDeleteError
  } = usePermissions();

  // Fetch permissions on component mount
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const handleCreatePermission = async (permissionData: any) => {
    try {
      await createPermission(permissionData);
      showSuccess('تم إنشاء الصلاحية بنجاح');
      setIsModalOpen(false);
      setSelectedPermission(undefined);
      await fetchPermissions();
    } catch (error) {
      // Handle error using StandardizedErrorHandler to show server message
      StandardizedErrorHandler.getInstance().handleError(error, 'PermissionsManagementNew.handleCreatePermission');
    }
  };

  const handleUpdatePermission = async (permissionData: any) => {
    try {
      await updatePermission(permissionData);
      showSuccess('تم تحديث الصلاحية بنجاح');
      setIsModalOpen(false);
      setSelectedPermission(undefined);
      await fetchPermissions();
    } catch (error) {
      // Handle error using StandardizedErrorHandler to show server message
      StandardizedErrorHandler.getInstance().handleError(error, 'PermissionsManagementNew.handleUpdatePermission');
    }
  };

  const handleDeletePermission = async (permissionId: number) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الصلاحية؟')) {
      try {
        await deletePermission(permissionId);
        showSuccess('تم حذف الصلاحية بنجاح');
        await fetchPermissions();
      } catch (error) {
        // Handle error using StandardizedErrorHandler to show server message
        StandardizedErrorHandler.getInstance().handleError(error, 'PermissionsManagementNew.handleDeletePermission');
      }
    }
  };

  const handleEditPermission = (permission: Permission) => {
    setSelectedPermission(permission);
    setIsModalOpen(true);
  };


  const handleOpenCreateModal = () => {
    setSelectedPermission(undefined);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPermission(undefined);
  };

  const handlePageChange = (page: number) => {
    const filters: PermissionFilters = {
      page,
      limit: pagination?.limit || 10
    };
    fetchPermissions(filters);
  };

  const handlePageSizeChange = (pageSize: number) => {
    const filters: PermissionFilters = {
      page: 1,
      limit: pageSize
    };
    fetchPermissions(filters);
  };


  return (
    <div className="space-y-6 flex-1 min-h-[calc(100vh-200px)] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <AppTitle className="mb-2">
            إدارة الصلاحيات
          </AppTitle>
        </div>
        <Button
          variant="primary"
          onClick={handleOpenCreateModal}
          disabled={loading || creating}
        >
          <Plus className="w-4 h-4 mr-2" />
          إضافة صلاحية
        </Button>
      </div>

      {/* Error is handled by StandardizedErrorHandler and shown as toast notification */}

      {createError && (
        <Card className="p-4 bg-red-50 border border-red-200">
          <AppText className="text-red-700">{createError}</AppText>
          <button
            onClick={clearCreateError}
            className="mt-2 text-sm text-red-600 hover:text-red-800"
          >
            إغلاق
          </button>
        </Card>
      )}

      {updateError && (
        <Card className="p-4 bg-red-50 border border-red-200">
          <AppText className="text-red-700">{updateError}</AppText>
          <button
            onClick={clearUpdateError}
            className="mt-2 text-sm text-red-600 hover:text-red-800"
          >
            إغلاق
          </button>
        </Card>
      )}

      {deleteError && (
        <Card className="p-4 bg-red-50 border border-red-200">
          <AppText className="text-red-700">{deleteError}</AppText>
          <button
            onClick={clearDeleteError}
            className="mt-2 text-sm text-red-600 hover:text-red-800"
          >
            إغلاق
          </button>
        </Card>
      )}


      {/* Permissions Table */}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-brand-500" />
            <AppCardTitle>
              قائمة الصلاحيات ({pagination?.total || 0})
            </AppCardTitle>
          </div>
        </div>

        <PermissionsTable
          data={permissions}
          page={pagination?.page || 1}
          pageSize={pagination?.limit || 10}
          totalCount={pagination?.total || 0}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onEdit={handleEditPermission}
          onDelete={handleDeletePermission}
          showPageSizeSelector={true}
          showPaginationInfo={true}
          loading={loading}
        />
      </Card>

      {/* Permission Modal */}
      <PermissionModal
        open={isModalOpen}
        onClose={handleCloseModal}
        permission={selectedPermission}
        onSubmit={selectedPermission ? handleUpdatePermission : handleCreatePermission}
        loading={creating || updating}
      />
    </div>
  );
};
